import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Download, Upload, CalendarRange, FileSpreadsheet, Loader2, AlertTriangle, ClipboardCheck } from 'lucide-react';
import { toast } from 'sonner';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { timesheetsAPI } from '../lib/api';
import { TimesheetUploadReview } from './TimesheetUploadReview';
import { TimesheetApproval } from './TimesheetApproval';
import { useCap } from '../components/auth/RequireCap';
import { HelpButton } from '../components/HelpButton';

// Pay week is Sunday → Saturday, company-wide (matches backend scheduling_schema).
function sundayOf(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  x.setDate(x.getDate() - x.getDay()); // getDay(): 0 = Sunday
  return x;
}
function iso(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function addDays(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}
function pretty(dstr) {
  const [y, m, d] = dstr.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

const STATUS_LABEL = {
  not_downloaded: { text: 'Not started', cls: 'bg-slate-100 text-slate-600 border-slate-200' },
  downloaded: { text: 'Downloaded', cls: 'bg-blue-50 text-blue-700 border-blue-200' },
  uploaded: { text: 'Uploaded', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  submitted: { text: 'Submitted', cls: 'bg-violet-50 text-violet-700 border-violet-200' },
  approved: { text: 'Approved', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  rejected: { text: 'Rejected', cls: 'bg-red-50 text-red-700 border-red-200' },
};
const STATUS_ORDER = ['not_downloaded', 'downloaded', 'uploaded', 'submitted', 'approved', 'rejected'];

export default function Timesheets() {
  const canApprove = useCap('scheduling', 'approve');
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkJob = useRef(searchParams.get('job') || null);
  const deepLinkWeek = searchParams.get('week');
  const validWeek = deepLinkWeek && /^\d{4}-\d{2}-\d{2}$/.test(deepLinkWeek) ? deepLinkWeek : null;
  const [weekStart, setWeekStart] = useState(() =>
    validWeek ? iso(sundayOf(new Date(validWeek.replace(/-/g, '/')))) : iso(sundayOf(new Date())));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(null);
  const [warnJob, setWarnJob] = useState(null); // job pending confirm (no posts/schedule)
  const [uploadJob, setUploadJob] = useState(null); // job open in upload/review dialog
  const [reviewJob, setReviewJob] = useState(null); // job open in approve/reject dialog
  const [statusFilter, setStatusFilter] = useState('all'); // company-wide weekly status filter

  const weekEnd = iso(addDays(new Date(weekStart.replace(/-/g, '/')), 6));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await timesheetsAPI.jobs(weekStart);
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load timesheets');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [weekStart]);

  useEffect(() => { load(); }, [load]);

  // Deep link (e.g. the rejection email: /timesheets?job=<number>&week=<date>):
  // once the week's jobs load, open that exact job — the review dialog for an
  // approver, otherwise the upload/fix dialog for the uploader — then drop the
  // params so it does not reopen on later reloads.
  useEffect(() => {
    if (!deepLinkJob.current || !data) return;
    const target = (data.jobs || []).find(
      (j) => String(j.job_number) === String(deepLinkJob.current));
    const wanted = deepLinkJob.current;
    deepLinkJob.current = null;
    setSearchParams({}, { replace: true });
    if (!target) {
      toast.error(`Job ${wanted} isn't on your list for this week.`);
      return;
    }
    if (canApprove && ['submitted', 'approved', 'rejected'].includes(target.status)) {
      setReviewJob(target);
    } else {
      setUploadJob(target);
    }
  }, [data, canApprove, setSearchParams]);

  const shiftWeek = (n) => setWeekStart(iso(addDays(new Date(weekStart.replace(/-/g, '/')), n * 7)));
  const onPickDate = (e) => { if (e.target.value) setWeekStart(iso(sundayOf(new Date(e.target.value.replace(/-/g, '/'))))); };

  const doDownload = async (job) => {
    setWarnJob(null);
    setDownloading(job.job_id);
    try {
      const res = await timesheetsAPI.download(job.job_id, weekStart);
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Timesheet_${job.job_number || 'job'}_${weekStart}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded timesheet for job ${job.job_number}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Download failed');
    } finally {
      setDownloading(null);
    }
  };

  // Item 6: warn BEFORE generating when the job has no posts or no schedule.
  const requestDownload = (job) => {
    if ((job.post_count || 0) === 0 || (job.scheduled_shift_count || 0) === 0) {
      setWarnJob(job);
    } else {
      doDownload(job);
    }
  };

  const warnReason = (job) => {
    if (!job) return '';
    const noPosts = (job.post_count || 0) === 0;
    const noSched = (job.scheduled_shift_count || 0) === 0;
    if (noPosts && noSched)
      return 'This job has no posts set up and no shifts scheduled this week. The sheet will have an empty Post list and only blank rows.';
    if (noPosts)
      return 'This job has no posts set up. The Post dropdown in the sheet will be empty.';
    return 'No shifts are scheduled for this job this week. The sheet will have no pre-filled rows — only blank rows to fill in.';
  };

  const jobs = data?.jobs || [];
  const statusCounts = STATUS_ORDER.reduce((acc, s) => {
    acc[s] = jobs.filter((j) => (j.status || 'not_downloaded') === s).length;
    return acc;
  }, {});
  const filteredJobs = statusFilter === 'all'
    ? jobs
    : jobs.filter((j) => (j.status || 'not_downloaded') === statusFilter);
  const fmtDate = (v) => {
    if (!v) return '—';
    const d = new Date(v);
    return isNaN(d) ? '—' : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="space-y-6" data-testid="timesheets-page">
      <div className="flex flex-col gap-1">
        <div className="flex items-start justify-between gap-3">
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
            <FileSpreadsheet className="w-6 h-6 text-blue-600" /> Weekly Timesheets
          </h1>
          <HelpButton module="timesheets" />
        </div>
        <p className="text-sm text-slate-500 max-w-3xl">
          Download the standard weekly sheet for a job, print it as the post sign-in sheet or fill it
          digitally, then upload it. For the payroll parallel test — these hours do not affect pay.
        </p>
      </div>

      {/* Week picker */}
      <Card className="p-4">
        <div className="flex flex-wrap items-center gap-3">
          <Button variant="outline" size="icon" onClick={() => shiftWeek(-1)} data-testid="week-prev-btn" aria-label="Previous week">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-slate-50 border border-slate-200" data-testid="week-range-label">
            <CalendarRange className="w-4 h-4 text-slate-500" />
            <span className="text-sm font-medium">{pretty(weekStart)} — {pretty(weekEnd)}</span>
            <span className="text-xs text-slate-400">(Sun–Sat)</span>
          </div>
          <Button variant="outline" size="icon" onClick={() => shiftWeek(1)} data-testid="week-next-btn" aria-label="Next week">
            <ChevronRight className="w-4 h-4" />
          </Button>
          <div className="flex items-center gap-2 ml-auto">
            <span className="text-xs text-slate-500">Jump to week:</span>
            <Input type="date" className="w-40" onChange={onPickDate} data-testid="week-date-picker" />
          </div>
        </div>
      </Card>

      {/* Jobs list */}
      <Card className="overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-slate-700" data-testid="jobs-count">
              {loading ? 'Loading…' : `${jobs.length} active job${jobs.length === 1 ? '' : 's'} this week`}
            </span>
          </div>
          {!loading && jobs.length > 0 && (
            <div className="flex flex-wrap gap-2" data-testid="status-filter-bar">
              <button
                onClick={() => setStatusFilter('all')}
                data-testid="status-filter-all"
                className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                  statusFilter === 'all'
                    ? 'bg-slate-800 text-white border-slate-800'
                    : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}>
                All ({jobs.length})
              </button>
              {STATUS_ORDER.map((s) => (
                <button
                  key={s}
                  onClick={() => setStatusFilter(s)}
                  data-testid={`status-filter-${s}`}
                  className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                    statusFilter === s
                      ? 'bg-slate-800 text-white border-slate-800'
                      : `${STATUS_LABEL[s].cls} hover:opacity-80`}`}>
                  {STATUS_LABEL[s].text} ({statusCounts[s]})
                </button>
              ))}
            </div>
          )}
        </div>

        {loading ? (
          <div className="p-10 flex items-center justify-center text-slate-400" data-testid="jobs-loading">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading jobs…
          </div>
        ) : jobs.length === 0 ? (
          <div className="p-10 text-center text-slate-500" data-testid="jobs-empty">
            No jobs are available to you for this week.
          </div>
        ) : filteredJobs.length === 0 ? (
          <div className="p-10 text-center text-slate-500" data-testid="jobs-filter-empty">
            No jobs with status “{STATUS_LABEL[statusFilter]?.text || statusFilter}” this week.
          </div>
        ) : (
          <div className="divide-y divide-slate-100" data-testid="jobs-list">
            {filteredJobs.map((job) => {
              const st = STATUS_LABEL[job.status] || STATUS_LABEL.not_downloaded;
              return (
                <div key={job.job_id} className="flex items-center gap-4 px-4 py-3 hover:bg-slate-50 transition-colors"
                     data-testid={`job-row-${job.job_id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-slate-800 truncate">
                      <span className="text-slate-500">#{job.job_number}</span> — {job.job_description || 'Unnamed job'}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {job.scheduled_shift_count} scheduled shift{job.scheduled_shift_count === 1 ? '' : 's'} this week
                      <span className="mx-1.5 text-slate-300">·</span>
                      <span data-testid={`job-last-action-${job.job_id}`}>Last action: {fmtDate(job.last_action_at)}</span>
                    </div>
                  </div>
                  <Badge variant="outline" className={st.cls} data-testid={`job-status-${job.job_id}`}>{st.text}</Badge>
                  <Button variant="outline" onClick={() => requestDownload(job)} disabled={downloading === job.job_id}
                          data-testid={`download-btn-${job.job_id}`} className="gap-2">
                    {downloading === job.job_id
                      ? <Loader2 className="w-4 h-4 animate-spin" />
                      : <Download className="w-4 h-4" />}
                    Download
                  </Button>
                  <Button onClick={() => setUploadJob(job)} data-testid={`upload-btn-${job.job_id}`} className="gap-2">
                    <Upload className="w-4 h-4" />
                    Upload
                  </Button>
                  {canApprove && ['submitted', 'approved', 'rejected'].includes(job.status) && (
                    <Button variant="outline" onClick={() => setReviewJob(job)}
                            data-testid={`review-btn-${job.job_id}`} className="gap-2">
                      <ClipboardCheck className="w-4 h-4" />
                      Review
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Card>

      <AlertDialog open={!!warnJob} onOpenChange={(o) => !o && setWarnJob(null)}>
        <AlertDialogContent data-testid="download-warning-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" /> Download this timesheet anyway?
            </AlertDialogTitle>
            <AlertDialogDescription data-testid="download-warning-reason">
              {warnReason(warnJob)}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="download-warning-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => doDownload(warnJob)} data-testid="download-warning-confirm">
              Download anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {uploadJob && (
        <TimesheetUploadReview job={uploadJob} weekStart={weekStart} open={!!uploadJob}
                               onClose={() => setUploadJob(null)} onDone={load} />
      )}

      {reviewJob && (
        <TimesheetApproval job={reviewJob} weekStart={weekStart} open={!!reviewJob}
                           onClose={() => setReviewJob(null)} onDone={load} />
      )}
    </div>
  );
}
